import React, { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { DATA_LINEAGE, RELATIONSHIPS, ML_MODELS } from "../../lib/dataAdmin";
import { listGold } from "../../lib/s3Api";
import { Tile, Badge } from "../Primitives";
import { LayerChip } from "../pipeline/Layer";
import { cn } from "../../lib/utils";

function Edge({ from, to, note, fromLayer, toLayer, testid }) {
  return (
    <div className="flex flex-wrap items-center gap-2 p-2.5 border border-border rounded-sm bg-surface-2/40" data-testid={testid}>
      {fromLayer && <LayerChip layer={fromLayer} />}<div className="font-mono-tabular text-[12px] font-semibold w-60 truncate">{from}</div>
      <ArrowRight size={12} className="text-primary shrink-0" />
      {toLayer && <LayerChip layer={toLayer} />}<div className="font-mono-tabular text-[12px] text-primary font-semibold w-60 truncate">{to}</div>
      <div className="text-[11px] text-muted flex-1 min-w-[160px] truncate">{note}</div>
    </div>
  );
}

// Live lineage from Bronze review (file → Silver/Gold table → Gold fact) followed by the platform's reference lineage.
export function Lineage({ dynamic }) {
  const [facts, setFacts] = useState([]);
  useEffect(() => { listGold().then(r => setFacts(r.models || [])).catch(() => {}); }, []);
  const live = dynamic.filter(t => t.kind === "promoted").flatMap(t => (t.sources || []).map(s => ({ from: s.filename, to: t.display, toLayer: t.layer, note: `${s.rows} rows · ${s.source === "s3" ? "S3" : "upload"} · reviewed and promoted` })));
  const gold = facts.flatMap(m => (m.source_summaries || []).map(s => ({ from: s.table, to: m.output_table, note: `${m.grain} Gold fact · ${m.state}` })));
  const grouped = DATA_LINEAGE.reduce((a, e) => ({ ...a, [e.type]: [...(a[e.type] || []), e] }), {});
  return (
    <div className="space-y-4" data-testid="tab-lineage">
      <Tile title="Live lineage · Bronze review" action={<Badge status="success">{live.length + gold.length} edges</Badge>}>
        <div className="space-y-1.5">
          {live.map((e, i) => <Edge key={`l${i}`} {...e} fromLayer="Bronze" testid={`lineage-live-${i}`} />)}
          {gold.map((e, i) => <Edge key={`g${i}`} {...e} fromLayer="Silver" toLayer="Gold" testid={`lineage-gold-${i}`} />)}
          {!live.length && !gold.length && <div className="text-[12px] text-muted" data-testid="lineage-live-empty">Nothing has been promoted through Bronze review yet.</div>}
        </div>
      </Tile>
      <Tile title="Reference lineage" action={<Badge status="info">{DATA_LINEAGE.length} edges</Badge>}>
        <div className="space-y-4">{Object.entries(grouped).map(([type, edges]) => (
          <div key={type}><div className="text-[10px] uppercase tracking-widest font-bold text-subtle mb-2">{type}</div>
            <div className="space-y-1.5">{edges.map((e, i) => <Edge key={i} from={e.source} to={e.target} note={e.transform} testid={`lineage-${type}-${i}`} />)}</div></div>))}</div>
      </Tile>
    </div>
  );
}

export function Relationships() {
  return (
    <Tile testid="tab-relationships" title="Foreign key relationships" action={<Badge status="success">{RELATIONSHIPS.filter(r => r.enforced).length}/{RELATIONSHIPS.length} enforced</Badge>} dense>
      <div className="overflow-x-auto"><table className="w-full text-sm">
        <thead><tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
          <th className="py-2 px-3 text-left">From</th><th className="py-2 px-3" /><th className="py-2 px-3 text-left">To</th><th className="py-2 px-3 text-left">Cardinality</th><th className="py-2 px-3 text-center">Enforced</th></tr></thead>
        <tbody className="font-mono-tabular">{RELATIONSHIPS.map((r, i) => (
          <tr key={i} className="border-b border-border" data-testid={`rel-${i}`}>
            <td className="py-2 px-3 font-semibold">{r.from}</td><td className="py-2 px-3"><ArrowRight size={12} className="text-primary" /></td>
            <td className="py-2 px-3 text-primary font-semibold">{r.to}</td><td className="py-2 px-3 font-sans"><Badge status="neutral">{r.type}</Badge></td>
            <td className="py-2 px-3 text-center">{r.enforced ? <Badge status="success">FK enforced</Badge> : <Badge status="warning">Soft ref</Badge>}</td></tr>))}</tbody></table></div>
    </Tile>
  );
}

export function MlRegistry() {
  return (
    <Tile testid="ml-models" title="ML model registry" action={<Badge status="success">{ML_MODELS.filter(m => m.status === "production").length} in prod</Badge>}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">{ML_MODELS.map(m => (
        <div key={m.id} className="p-2.5 border border-border rounded-sm bg-surface-2/40" data-testid={`ml-${m.id}`}>
          <div className="flex items-start justify-between gap-2 mb-1"><div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{m.name}</div><div className="text-[10px] text-muted">{m.framework} · {m.version}</div></div>
            <Badge status={m.status === "production" ? "success" : m.status === "pilot" ? "info" : "neutral"}>{m.status}</Badge></div>
          <div className="flex items-center justify-between text-[10px] font-mono-tabular"><span className="text-muted">Owner: {m.owner}</span>
            <span className={cn("font-semibold", m.accuracy >= 0.85 ? "text-success" : m.accuracy >= 0.75 ? "text-info" : "text-warning")}>Acc {(m.accuracy * 100).toFixed(1)}%</span></div>
        </div>))}</div>
    </Tile>
  );
}
